import { createZip } from './zip.js';
import { buildSocialSharePayload, listSocialPlatforms } from './social-share.js';
import { currentLocale, tr } from '../services/i18n.js';

// Libellés français de la confiance du modèle (alignés sur l'UI debrief).
const CONFIDENCE_LABELS = {
  low: () => tr('faible', 'low'),
  medium: () => tr('moyenne', 'medium'),
  high: () => tr('élevée', 'high')
};

// Libellés français des types de changement entre images clés (alignés sur l'UI).
const CHANGE_TYPE_LABELS = {
  ball_progression: () => tr('Progression du ballon', 'Ball progression'),
  attacking_shape: () => tr('Structure offensive', 'Attacking shape'),
  defensive_line: () => tr('Ligne défensive', 'Defensive line'),
  pressing: 'Pressing',
  space: () => tr('Espaces', 'Spaces'),
  player_movement: () => tr('Mouvement de joueur', 'Player movement'),
  set_piece_setup: () => tr('Mise en place phase arrêtée', 'Set-piece setup'),
  restart_delivery: () => tr('Frappe / remise en jeu', 'Restart delivery')
};

const GAME_PHASE_LABELS = {
  open_play: () => tr('Jeu ouvert', 'Open play'),
  corner: 'Corner',
  free_kick_direct: () => tr('Coup franc direct', 'Direct free kick'),
  free_kick_indirect: () => tr('Coup franc indirect', 'Indirect free kick'),
  penalty: 'Penalty',
  throw_in: () => tr('Touche', 'Throw-in'),
  goal_kick: () => tr('Six mètres', 'Goal kick'),
  kick_off: () => tr('Engagement', 'Kick-off'),
  offside_restart: () => tr('Reprise hors-jeu', 'Offside restart'),
  unknown: () => tr('Phase incertaine', 'Unknown phase')
};

// Libellés français des champs d'analyse par image (alignés sur l'UI debrief).
const FRAME_FIELD_LABELS = {
  players: () => tr('Position des joueurs', 'Player positions'),
  shape: () => tr('Schéma', 'Shape'),
  imbalance: () => tr('Déséquilibre', 'Imbalance'),
  pressure: 'Impact'
};

// Restitue l'analyse d'une image (Avant/Après) en Markdown, labels en français.
// Renvoie '' si l'analyse est absente (anciennes analyses) : la synthèse globale
// nommée « Ce que ça change » prend alors le relais, sans inventer de détail.
function frameAnalysisToMarkdown(label, analysis) {
  if (!analysis || typeof analysis !== 'object') return '';
  const rows = Object.entries(FRAME_FIELD_LABELS)
    .filter(([key]) => analysis[key] && String(analysis[key]).trim())
    .map(([key, fieldLabel]) => `  - ${typeof fieldLabel === 'function' ? fieldLabel() : fieldLabel} : ${String(analysis[key]).trim()}`);
  if (!rows.length) return '';
  return `- **${label}**\n${rows.join('\n')}\n`;
}

// Construit un nom de fichier sûr à partir d'un titre.
function slugify(value) {
  return String(value || tr('analyse', 'analysis'))
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')
    .slice(0, 50) || 'analyse';
}

// Génère un Markdown lisible à partir d'un débrief Supabase.
export function debriefToMarkdown(debrief) {
  const raw = debrief.ai_raw_response || {};
  const lines = [];
  lines.push(`# ${debrief.title || tr('Analyse sans titre', 'Untitled analysis')}`);
  lines.push('');
  lines.push(`*${tr('Exporté le', 'Exported on')} ${new Date().toLocaleString(currentLocale())}*`);
  if (debrief.user?.pseudo) lines.push(`*${tr('Auteur', 'Author')}: @${debrief.user.pseudo}*`);
  lines.push('');

  if (raw.segment && (raw.segment.start != null || raw.segment.end != null)) {
    lines.push(`**${tr('Segment analysé', 'Analyzed segment')}:** ${raw.segment.start ?? 0}s → ${raw.segment.end ?? '?'}s`);
    lines.push('');
  }

  // Convertit un index de frame en seconde dans la fenêtre. Analyses récentes :
  // instant EXACT réellement capturé (raw.frame_times), borne de fin incluse (cas
  // général depuis computeFrameSampleTimes). Anciennes analyses (sans frame_times) :
  // repli sur l'ANCIENNE formule d'extraction (s'arrêtait avant `end`), partagée avec
  // la recapture UI (debrief.js) pour que les instants correspondent aux images héritées.
  const frameToSec = (idx) => {
    const i = Number.isFinite(idx) ? idx : 0;
    if (Array.isArray(raw.frame_times) && Number.isFinite(raw.frame_times[i])) {
      return Number(raw.frame_times[i]).toFixed(1);
    }
    const fc = Number(raw.frame_count) > 0 ? Number(raw.frame_count) : 8;
    const seg = raw.segment && Number.isFinite(raw.segment.start) ? raw.segment : null;
    const start = seg ? seg.start : 0;
    const end = seg ? seg.end : 15;
    const span = Math.max(0.1, end - start);
    const step = span / fc;
    return Math.min(end - 0.01, start + step * i + 0.1).toFixed(1);
  };
  if (raw.confidence) {
    const confidence = CONFIDENCE_LABELS[raw.confidence];
    lines.push(`**${tr('Confiance du modèle', 'Model confidence')}:** ${typeof confidence === 'function' ? confidence() : confidence || raw.confidence}`);
    lines.push('');
  }

  lines.push(`## ${tr('Résumé', 'Summary')}`);
  lines.push(debrief.summary || tr('_(vide)_', '_(empty)_'));
  lines.push('');

  if (debrief.key_moment) {
    lines.push(`## ${tr('Moment décisif', 'Decisive moment')}`);
    lines.push(debrief.key_moment);
    lines.push('');
  }

  const obs = raw.observations;
  if (obs && typeof obs === 'object') {
    lines.push(`## ${tr('Observations factuelles', 'Factual observations')}`);
    for (const [k, v] of Object.entries(obs)) {
      if (v == null || v === '') continue;
      lines.push(`- **${k}** : ${v}`);
    }
    lines.push('');
  }

  if (raw.game_phase && typeof raw.game_phase === 'object') {
    const phase = raw.game_phase;
    const label = GAME_PHASE_LABELS[phase.type];
    lines.push(`## ${tr('Phase de jeu', 'Game phase')}`);
    lines.push(`**${tr('Type', 'Type')}:** ${phase.label || (typeof label === 'function' ? label() : label) || phase.type || tr('Phase incertaine', 'Unknown phase')}`);
    if (phase.confidence) {
      const confidence = CONFIDENCE_LABELS[phase.confidence];
      lines.push(`**${tr('Confiance', 'Confidence')}:** ${typeof confidence === 'function' ? confidence() : confidence || phase.confidence}`);
    }
    if (phase.evidence) lines.push(`**${tr('Indices visibles', 'Visible clues')}:** ${phase.evidence}`);
    if (phase.analysis_focus) lines.push(`**${tr("Impact sur l'analyse", 'Analysis impact')}:** ${phase.analysis_focus}`);
    lines.push('');
  }

  if (raw.interpretation) {
    lines.push(`## ${tr('Interprétation tactique', 'Tactical interpretation')}`);
    lines.push(raw.interpretation);
    lines.push('');
  }

  const elements = debrief.tactical_elements || [];
  if (elements.length) {
    lines.push(`## ${tr('Éléments tactiques', 'Tactical elements')}`);
    elements.forEach(el => {
      lines.push(`- **${el.type || 'key'}** : ${el.description || ''}`);
    });
    lines.push('');
  }

  const comparisons = raw.key_frame_comparisons || [];
  if (comparisons.length) {
    lines.push(`## ${tr("Comparaisons d'images clés", 'Key frame comparisons')}`);
    comparisons.forEach((c, i) => {
      lines.push(`### ${tr('Comparaison', 'Comparison')} ${i + 1}`);
      lines.push(`*${tr('image', 'frame')} ${c.from_frame} (≈${frameToSec(c.from_frame)}s) → ${tr('image', 'frame')} ${c.to_frame} (≈${frameToSec(c.to_frame)}s)*`);
      const changeType = CHANGE_TYPE_LABELS[c.change_type];
      if (c.change_type) lines.push(`- Type: ${typeof changeType === 'function' ? changeType() : changeType || c.change_type}`);
      // Analyse image par image (façon plateau). Absente sur les anciennes analyses.
      const frameBlock = frameAnalysisToMarkdown(tr('Avant', 'Before'), c.from_analysis) + frameAnalysisToMarkdown(tr('Après', 'After'), c.to_analysis);
      if (frameBlock) lines.push(frameBlock.trimEnd());
      // Synthèse globale de la comparaison, nommée clairement.
      if (c.description) lines.push(`- **${tr("Ce que ça change", 'What changes')}** : ${c.description}`);
      // Analyse dense « magazine » + points tactiques ciblés (analyses récentes).
      if (c.tactical_breakdown && String(c.tactical_breakdown).trim()) {
        lines.push('');
        lines.push(String(c.tactical_breakdown).trim());
        lines.push('');
      }
      if (c.attacking_structure && String(c.attacking_structure).trim()) lines.push(`- **${tr('Construction offensive', 'Attacking structure')}** : ${String(c.attacking_structure).trim()}`);
      if (c.defensive_structure && String(c.defensive_structure).trim()) lines.push(`- **${tr('Bloc défensif', 'Defensive block')}** : ${String(c.defensive_structure).trim()}`);
      if (c.destabilization && String(c.destabilization).trim()) lines.push(`- **${tr('Déstabilisation', 'Destabilization')}** : ${String(c.destabilization).trim()}`);
      if (c.coaching_point && String(c.coaching_point).trim()) lines.push(`- **${tr('À retenir', 'Takeaway')}** : ${String(c.coaching_point).trim()}`);
      if (c.observations) lines.push(`- ${tr('Indices visibles', 'Visible clues')} : ${c.observations}`);
      lines.push('');
    });
  }

  return lines.join('\n');
}

// Empaquette le débrief dans un ZIP (markdown + metadata JSON) et lance le téléchargement.
export function exportDebriefZip(debrief) {
  const markdown = debriefToMarkdown(debrief);
  const socialCopy = debriefSocialCopy(debrief);
  const metadata = {
    id: debrief.id,
    title: debrief.title,
    summary: debrief.summary,
    key_moment: debrief.key_moment,
    created_at: debrief.created_at,
    author: debrief.user?.pseudo || null,
    tactical_elements: debrief.tactical_elements || [],
    players_positions: debrief.players_positions || [],
    ball_position: debrief.ball_position || {},
    arrows: debrief.arrows || [],
    analysis: debrief.ai_raw_response || null,
    exported_at: new Date().toISOString()
  };

  const blob = createZip([
    { name: 'analyse.md', data: markdown },
    { name: 'social-copy.txt', data: socialCopy },
    { name: 'metadata.json', data: JSON.stringify(metadata, null, 2) }
  ]);

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `fooview-${slugify(debrief.title)}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function debriefSocialCopy(debrief, url = '') {
  return listSocialPlatforms()
    .map((platform) => {
      const payload = buildSocialSharePayload(debrief, { platform: platform.id, url });
      return [
        `## ${platform.label}`,
        `${tr('Format image', 'Image format')}: ${payload.format.label} ${payload.format.width}x${payload.format.height}`,
        '',
        payload.text
      ].join('\n');
    })
    .join('\n\n---\n\n');
}
